import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import SwapVertOutlined from '@mui/icons-material/SwapVertOutlined';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import { DatePicker } from '@practium/ui';
import { formatDateFromISO } from '@practium/shared';
import { PERIOD_OPTIONS, STATUS_OPTIONS, SORT_OPTIONS } from '../../constants/filterOptions';
import styles from './SupportFilters.module.css';

interface FilterChipProps {
	icon: React.ReactNode;
	value: string;
	options: readonly { value: string; label: string }[];
	onChange: (value: string) => void;
	dateFrom?: string;
	dateTo?: string;
	onDateRangeChange?: (from: string, to: string) => void;
}

const FilterChip = ({
	icon,
	value,
	options,
	onChange,
	dateFrom = '',
	dateTo = '',
	onDateRangeChange,
}: FilterChipProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isDatePanelOpen, setIsDatePanelOpen] = useState(false);
	const [localFrom, setLocalFrom] = useState(dateFrom);
	const [localTo, setLocalTo] = useState(dateTo);
	const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
	const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const selected = options.find((o) => o.value === value);

	const chipLabel =
		value === 'custom' && dateFrom
			? `${formatDateFromISO(dateFrom)}${dateTo ? ` — ${formatDateFromISO(dateTo)}` : ''}`
			: selected?.label;

	useEffect(() => {
		setLocalFrom(dateFrom);
		setLocalTo(dateTo);
	}, [dateFrom, dateTo]);

	useEffect(() => {
		if (!isOpen && !isDatePanelOpen) return;
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			const inWrapper = wrapperRef.current?.contains(target);
			const inDropdown = dropdownRef.current?.contains(target);
			const inPanel = panelRef.current?.contains(target);
			if (!inWrapper && !inDropdown && !inPanel) {
				setIsOpen(false);
				setIsDatePanelOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, isDatePanelOpen]);

	useEffect(() => {
		if (!isOpen && !isDatePanelOpen) return;
		const updatePos = () => {
			if (!wrapperRef.current) return;
			const rect = wrapperRef.current.getBoundingClientRect();
			const pos = { top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX };
			if (isOpen) setDropdownPos(pos);
			if (isDatePanelOpen) setPanelPos(pos);
		};
		updatePos();
		const closeOnScroll = () => { setIsOpen(false); setIsDatePanelOpen(false); };
		window.addEventListener('scroll', closeOnScroll, true);
		window.addEventListener('resize', updatePos);
		return () => {
			window.removeEventListener('scroll', closeOnScroll, true);
			window.removeEventListener('resize', updatePos);
		};
	}, [isOpen, isDatePanelOpen]);

	const handleSelect = (optValue: string) => {
		onChange(optValue);
		setIsOpen(false);
		if (optValue === 'custom') {
			setIsDatePanelOpen(true);
		} else {
			setIsDatePanelOpen(false);
		}
	};

	const handleApply = () => {
		if (onDateRangeChange) onDateRangeChange(localFrom, localTo);
		setIsDatePanelOpen(false);
	};

	return (
		<div className={styles.chipWrapper} ref={wrapperRef}>
			<button
				type="button"
				className={`${styles.chip} ${isOpen || isDatePanelOpen ? styles.chipOpen : ''}`}
				onClick={() => {
					setIsOpen((p) => !p);
					setIsDatePanelOpen(false);
				}}
			>
				<div className={styles.chipIcon}>{icon}</div>
				<span className={styles.chipLabel}>{chipLabel}</span>
				<KeyboardArrowDownOutlined
					className={`${styles.chipArrow} ${isOpen || isDatePanelOpen ? styles.chipArrowOpen : ''}`}
				/>
			</button>

			{isOpen && dropdownPos && createPortal(
				<div
					ref={dropdownRef}
					className={styles.dropdown}
					style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left }}
				>
					{options.map((opt) => (
						<button
							key={opt.value}
							type="button"
							className={`${styles.option} ${opt.value === value ? styles.optionSelected : ''}`}
							onClick={() => handleSelect(opt.value)}
						>
							{opt.label}
						</button>
					))}
				</div>,
				document.body
			)}

			{isDatePanelOpen && onDateRangeChange && panelPos && createPortal(
				<div
					ref={panelRef}
					className={styles.datePanel}
					style={{ position: 'fixed', top: panelPos.top, left: panelPos.left }}
				>
					<span className={styles.datePanelTitle}>Selecione o intervalo</span>
					<div className={styles.dateFields}>
						<div className={styles.dateField}>
							<span className={styles.dateLabel}>De</span>
							<DatePicker
								value={localFrom}
								onChange={(v) => {
									setLocalFrom(v);
									if (localTo && v > localTo) setLocalTo('');
								}}
								placeholder="DD/MM/AAAA"
							/>
						</div>
						<div className={styles.dateField}>
							<span className={styles.dateLabel}>Até</span>
							<DatePicker
								value={localTo}
								onChange={(v) => {
									setLocalTo(v);
									if (localFrom && v < localFrom) setLocalFrom('');
								}}
								placeholder="DD/MM/AAAA"
							/>
						</div>
					</div>
					<button
						type="button"
						className={styles.applyBtn}
						onClick={handleApply}
						disabled={!localFrom}
					>
						Aplicar
					</button>
				</div>,
				document.body
			)}
		</div>
	);
};

interface SupportFiltersProps {
	period: string;
	onPeriodChange: (v: string) => void;
	statusFilter: string;
	onStatusChange: (v: string) => void;
	statusOptions?: readonly { value: string; label: string }[];
	sortOrder: string;
	onSortChange: (v: string) => void;
	hideSortOrder?: boolean;
	totalCount: number;
	customDateFrom?: string;
	customDateTo?: string;
	onDateRangeChange?: (from: string, to: string) => void;
}

export const SupportFilters = ({
	period,
	onPeriodChange,
	statusFilter,
	onStatusChange,
	statusOptions = STATUS_OPTIONS,
	sortOrder,
	onSortChange,
	hideSortOrder = false,
	totalCount,
	customDateFrom,
	customDateTo,
	onDateRangeChange,
}: SupportFiltersProps) => (
	<div className={styles.filters}>
		<div className={styles.chips}>
			<FilterChip
				icon={<CalendarTodayOutlined className={styles.icon} />}
				value={period}
				options={PERIOD_OPTIONS}
				onChange={onPeriodChange}
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onDateRangeChange={onDateRangeChange}
			/>
			<div className={styles.divider} />
			<FilterChip
				icon={<span className={`material-symbols-outlined ${styles.icon}`}>page_info</span>}
				value={statusFilter}
				options={statusOptions}
				onChange={onStatusChange}
			/>
			{!hideSortOrder && (
				<>
					<div className={styles.divider} />
					<FilterChip
						icon={<SwapVertOutlined className={styles.icon} />}
						value={sortOrder}
						options={SORT_OPTIONS}
						onChange={onSortChange}
					/>
				</>
			)}
		</div>
		<span className={styles.count}>{totalCount} Perguntas</span>
	</div>
);
